import { filterPlants } from './plantFilters';

/**
 * Querying the catalogue: pure, so the browse screen holds a query string and a
 * category key and nothing else.
 *
 * A plant record is language-independent (`catalogue.json`) and its prose lives
 * in a per-language file (`prose.<lang>.json`, SPEC-002 D-6). Search has to span
 * both — a gardener types the common name, which is prose — so every function
 * here takes the two together rather than reaching for a module-level singleton.
 */

/**
 * Fold accents and case so a Hungarian or German search term matches.
 *
 * `Évelő` typed as `evelo`, `Lamb's Ear` as `lambs ear`. Doing this at query
 * time rather than storing a second folded field keeps the generated catalogue
 * free of fields that exist only to serve one screen.
 */
export function foldForSearch(value) {
    return String(value ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/gu, '')
        .toLowerCase()
        .replace(/['’]/gu, '')
        .trim();
}

/**
 * Join a plant to its prose. Prose for a language that has not been translated
 * yet falls back to English, so a missing `prose.de.json` renders an English
 * description rather than an empty card.
 *
 * Per record, not per field: a translated entry stands in for the English one
 * whole. `src/contentTranslations.test.js` is what keeps a half-filled entry
 * from quietly rendering as blanks.
 */
export function joinPlantProse(plant, prose, fallbackProse = {}) {
    return { ...plant, prose: prose?.[plant.id] || fallbackProse[plant.id] || null };
}

export function joinCatalogue(plants, prose, fallbackProse = {}) {
    return plants.map((plant) => joinPlantProse(plant, prose, fallbackProse));
}

/**
 * The one line the hero shows over the artwork (SPEC-003 D-3).
 *
 * 21 of the 22 packs carry a `tagline` written for exactly this. Lavender — the
 * plant the approved reference was drawn from — is the shape-A pack that does
 * not, so rather than leave that hero blank the line falls back to the first
 * sentence of the description. A derivation from the pack's own prose; About
 * still shows the description whole.
 */
export function plantTagline(prose) {
    if (prose?.tagline) {
        return prose.tagline;
    }

    const description = prose?.description;

    if (!description) {
        return null;
    }

    const [firstSentence] = description.match(/^[^.!?]+[.!?]/u) || [];

    return firstSentence || description;
}

/**
 * Match on common and botanical name (FR-03).
 *
 * Substring rather than prefix: a gardener looking for Purple Coneflower types
 * "coneflower" more often than "purple". Every term must hit, so "purple cone"
 * narrows instead of widening.
 */
export function searchPlants(plants, query) {
    const terms = foldForSearch(query).split(/\s+/u).filter(Boolean);

    if (terms.length === 0) {
        return plants;
    }

    return plants.filter((plant) => {
        const haystack = foldForSearch(`${plant.prose?.commonName ?? ''} ${plant.botanicalName}`);

        return terms.every((term) => haystack.includes(term));
    });
}

/** Alphabetical by the name the user actually reads, which is per-language. */
export function sortByCommonName(plants, languageCode = 'en') {
    return [...plants].sort((left, right) => (
        String(left.prose?.commonName ?? left.id)
            .localeCompare(String(right.prose?.commonName ?? right.id), languageCode)
    ));
}

/**
 * Search and filters are cumulative and independent (SPEC-011): clearing the
 * query leaves the filters standing and vice versa, which is only true because
 * neither is expressed in terms of the other.
 */
export function selectPlants(plants, { query, filters, languageCode } = {}) {
    return sortByCommonName(searchPlants(filterPlants(plants, filters), query), languageCode);
}

export function findPlantById(plants, plantId) {
    return plants.find((plant) => plant.id === plantId) || null;
}

/** How many plants each category would show, for the filter row's counts. */
export function countByCategory(plants) {
    return plants.reduce((counts, plant) => {
        counts[plant.category] = (counts[plant.category] || 0) + 1;

        return counts;
    }, {});
}
